import React, { useEffect, useState, useMemo, useRef } from "react";
import { Table, Dropdown, Icon, Popup } from "semantic-ui-react";
import { DualAxes, Line, Mix } from "@ant-design/plots";
import { Permission, /* Energyform, Form, Productionform */ } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts/LanguageContext";
import ResumenEnergia from "./ResumenEnergia";
import TableGraf from "./TableGraf";
import { CollapsibleSection, LoadingSpinner } from "../../../../components/Shared";

import {
  energyKPIsCodes,
  energyFormulasIndicators,
  determineEnergyUnits,
  energyCodes,
  convertUnitsFinal,
  convertToMWh,
} from "../../../../utils/dataAndFormulas/energyForms";

import "./EnergyKPIsDashBoard.scss";
import {
  convertEnergy2,
  CONVERSION_FACTORS_TO_MWH,
  FUELS,
} from "../../../../utils/dataAndFormulas/convertEnergy";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ExportPDFMenuButton from "./pdfReport";

const permissionController = new Permission();
// const energyController = new Energyform();
// const formController = new Form();
// const productionController = new Productionform();

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function EnergyKPIsDashBoard(props) {
  const { site, sharedProductionForms, sharedWorkersByPeriod } = props;
  // console.log("🏢 EnergyKPIsDashBoard renderizado con site:", site);

  const {
    user: { role },
    accessToken,
  } = useAuth();

  const { language, translations } = useLanguage();

  const t = (key) => translations[key] || key; // Función para obtener la traducción

  const [permissionActive, setPermissionsActive] = useState([]);
  const [baseline, setbaseline] = useState("");
  const [valueBaseline, setValueBaseline] = useState(null);
  const [indicatorsLineBase, setIndicatorsLineBase] = useState([]);
  // const isAdmin = (role? role.name : "") === "admin";

  const [data, setData] = useState([]);
  const [totales, setTotales] = useState({});
  const [conceptos, setConceptos] = useState([]);
  const [configFormData, setConfigFormData] = useState(null);

  const [loadingCount, setLoadingCount] = useState(0);
  const isLoading = loadingCount > 0;


  const [lastYears, setLastYears] = useState({}); // resultados calculados
  const [totalesByFilters, setTotalesByFilters] = useState({}); // resultados calculados
  const [totalesProductionsByFilters, setTotalesProductionsByFilters] =
    useState({}); // resultados de producción por mes
  const [workersTotals, setWorkersTotals] = useState({}); // promedio de trabajadores por año
  const [workersTotalsByPeriod, setWorkersTotalsByPeriod] = useState({}); // resultados de trabajadores por periodo

  const [selectedUnits, setSelectedUnits] = useState([]);

  const startLoading = () => setLoadingCount((prev) => prev + 1);
  const stopLoading = () => setLoadingCount((prev) => Math.max(prev - 1, 0));

  //declaración estado de filtros
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
  });

  useEffect(() => {
    (async () => {
      startLoading();
      try {
        setPermissionsActive([]);
        if (role) {
          const response = await permissionController.getPermissionsByRole(
            accessToken,
            role._id,
            true,
          );
          setPermissionsActive(response);
        }
      } catch (error) {
        energyKPIsCodes.error(error);
        setPermissionsActive([]);
      } finally {
        stopLoading();
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      startLoading();
      try {
        setData([]);

        if (site) {
          const prueba = await energyController.getEnergyFormsByLineBaseYears(
            accessToken,
            site,
          );

          setIndicatorsLineBase(prueba.energyForms.indicators);
          const yearsLineaBase = prueba.energyForms.years;
          const currentYear = new Date().getFullYear();

          // Generar los cinco últimos años
          const yearsUltimos5 = [
            currentYear,
            currentYear - 1,
            currentYear - 2,
            currentYear - 3,
            currentYear - 4,
          ].map(String);

          // Combina ambos arrays y elimina duplicados utilizando Set
          const añosCombinados = [
            ...new Set([...yearsLineaBase, ...yearsUltimos5]),
          ];
          const response = await energyController.getEnergyFormsByLastYears(
            accessToken,
            site,
            añosCombinados,
          );

          setData(response.energyForms); // obtengo los formularios de los ultimos 5 años
        }
      } catch (error) {
        console.error(error);
        setData([]);
      } finally {
        stopLoading();
      }
    })();
  }, [site]);

  useEffect(() => {
    (async () => {
      startLoading();
      setbaseline("");
      try {
        if (site) {
          const response = await formController.getLastBaselineByFormBySite(
            accessToken,
            site,
            "production",
          );

          if (response.code === 200) {
            setbaseline(response.baseline);

            //Para saber el total de los años de baseline sin necesidad de andar consultando varias veces
            const resultado = await Promise.all(
              Object.entries(response.baseline)
                .filter(([anio, valor]) => valor !== null) // Filtra por los valores no nulos
                .map(async ([anio, valor]) => {
                  let a = await getValueBaseLine(anio, valor); // Llamada a la función asincrónica
                  return { [anio]: a }; // Devuelve el año y el valor como un objeto
                }),
            );

            // Combina todos los objetos en un único objeto final
            const resultadoFinal = resultado.reduce((acc, obj) => {
              return { ...acc, ...obj }; // Combina los objetos
            }, {});

            setValueBaseline(resultadoFinal);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        stopLoading();
      }
    })();
  }, [site]);

  // Obtener configuración de campos activos del formulario
  useEffect(() => {
    (async () => {
      try {
        if (site) {
          // console.log("🔄 Cargando configFormData para sitio:", site);
          const response = await formController.getFormsBySite(
            accessToken,
            site,
            "energy",
            true,
          );
          // console.log("📥 Respuesta de configFormData:", response);

          // La respuesta es directamente un array de formularios
          if (Array.isArray(response) && response.length > 0) {
            // console.log("✅ ConfigFormData cargado exitosamente:", response[0]);
            // console.log("📋 Campos en configFormData:", response[0]?.fields?.map(f => ({ name: f.name, active: f.active })));
            setConfigFormData(response[0]);
          } else {
            console.warn(
              "⚠️ No se pudo cargar configFormData, respuesta vacía o inválida",
            );
            setConfigFormData(null);
          }
        }
      } catch (error) {
        console.error("❌ Error al obtener configFormData:", error);
        setConfigFormData(null);
      }
    })();
  }, [site]);

  // Función que obtiene los campos fuente de un KPI basándose en energyFormulasIndicators
  const getKPISourceFields = (kpiName) => {
    // Mapeo basado en la lógica real de energyFormulasIndicators
    const kpiSourceMap = {
      // Electricidad estándar
      electricity_standard_combined_kpis: ["electricity_standard_combined"],
      electricity_standard_domestic_kpis: ["electricity_standard_domestic"],
      electricity_standard_production_kpis: ["electricity_standard_production"],

      // Energías Renovables PPA
      renewable_energies_ppa_combined_kpis: ["renewable_energies_ppa_combined"],
      renewable_energies_ppa_domestic_kpis: ["renewable_energies_ppa_domestic"],
      renewable_energies_ppa_production_kpis: [
        "renewable_energies_ppa_production",
      ],

      // Energías renovables on-site
      renewable_energies_on_site_combined_kpis: [
        "renewable_energies_on_site_combined",
      ],
      renewable_energies_on_site_domestic_kpis: [
        "renewable_energies_on_site_domestic",
      ],
      renewable_energies_on_site_production_kpis: [
        "renewable_energies_on_site_production",
      ],

      // Gas Natural
      natural_gas_combined_kpis: ["natural_gas_combined"],
      natural_gas_cogeneration_kpis: ["natural_gas_cogeneration"],
      natural_gas_production_operations_kpis: [
        "natural_gas_production_operations",
      ],
      natural_gas_domestic_kpis: ["natural_gas_domestic"],

      // GLP
      glp_combined_kpis: ["glp_combined"],
      glp_cogeneration_kpis: ["glp_cogeneration"],
      glp_production_operations_kpis: ["glp_production_operations"],
      glp_domestic_kpis: ["glp_domestic"],

      // Combustibles fósiles
      heavy_fuel_oil_total_kpis: ["heavy_fuel_oil_total"],
      light_fuel_oil_total_kpis: ["light_fuel_oil_total"],
      coal_total_kpis: ["coal_total"],

      // Diesel
      diesel_combined_kpis: ["diesel_combined"],
      diesel_cogeneration_kpis: ["diesel_cogeneration"],
      diesel_production_operations_kpis: ["diesel_production_operations"],
      diesel_forklifts_kpis: ["diesel_forklifts"],

      // Biodiesel
      biodiesel_combined_kpis: ["biodiesel_combined"],
      biodiesel_cogeneration_kpis: ["biodiesel_cogeneration"],
      biodiesel_production_operations_kpis: ["biodiesel_production_operations"],
      biodiesel_forklifts_kpis: ["biodiesel_forklifts"],

      // Gasolina
      gasoline_combined_kpis: ["gasoline_combined"],
      gasoline_cogeneration_kpis: ["gasoline_cogeneration"],
      gasoline_production_operations_kpis: ["gasoline_production_operations"],
      gasoline_forklifts_kpis: ["gasoline_forklifts"],

      // Biofuel
      biofuel_combined_kpis: ["biofuel_combined"],
      biofuel_cogeneration_kpis: ["biofuel_cogeneration"],
      biofuel_production_operations_kpis: ["biofuel_production_operations"],
      biofuel_forklifts_kpis: ["biofuel_forklifts"],

      // Otros
      biomass_total_kpis: ["biomass_total"],
      steam_total_kpis: ["steam_total"],

      // Porcentajes renovables
      biodiesel_renewable_percentage_kpis: ["biodiesel_renewable_percentage"],
      gasoline_renewable_percentage_kpis: ["gasoline_renewable_percentage"],
      irec_certificates_kpis: ["irec_certificates"],

      // KPIs calculados/agregados (no filtrar por campos individuales)
      total_energy_kpis: null, // Calculado a partir de total_energy_consumption_kpis
      total_energy_consumption_kpis: null, // Agregación de múltiples campos
      total_energy_cost_consumption_kpis: null, // Agregación de costos
      energy_cost_kpis: null, // Calculado
      renewable_energy_percentage_kpis: null, // Calculado
      non_renewable_energy_percentage_kpis: null, // Calculado
      renewable_with_irec_percentage_kpis: null, // Calculado
      total_electricity_kpis: null, // Agregación
      total_electricity_cost_kpis: null, // Agregación
      electricity_per_unit_kpis: null, // Calculado
      electricity_cost_per_unit_kpis: null, // Calculado
      total_fuels_kpis: null, // Agregación
      total_fuels_cost_kpis: null, // Agregación
      fuels_per_unit_kpis: null, // Calculado
    };

    return kpiSourceMap[kpiName];
  };

  // Memoizar campos de KPIs - solo recalcular cuando cambie configFormData
  const memoizedFieldsKPIs = useMemo(() => {
    const fields = new Set();

    Object.keys(energyKPIsCodes).forEach((key) => {
      const sourceFields = getKPISourceFields(key);

      // Si no tiene campos fuente (es un KPI calculado/agregado), siempre agregarlo
      if (sourceFields === null) {
        fields.add(key);
        return;
      }

      // Si hay configFormData, verificar si TODOS los campos fuente están activos
      if (configFormData?.fields && configFormData.fields.length > 0) {
        const allSourceFieldsActive = sourceFields.every((sourceFieldName) => {
          const fieldConfig = configFormData.fields.find(
            (f) => f.name === sourceFieldName,
          );
          return fieldConfig && fieldConfig.active !== false;
        });

        if (allSourceFieldsActive) {
          fields.add(key);
        }
      } else {
        // Si no hay configFormData, mostrar todos los campos (comportamiento por defecto)
        fields.add(key);
      }
    });

    return Array.from(fields);
  }, [configFormData]);

  // Memoizar campos de códigos - es constante, solo calcular una vez
  const memoizedFieldsCode = useMemo(() => {
    return Object.keys(energyCodes);
  }, []);

  // Funciones wrapper para compatibilidad con código existente
  const determineFieldsKPIs = () => memoizedFieldsKPIs;
  const determineFieldsCode = () => memoizedFieldsCode;

  useEffect(() => {
    (async () => {
      startLoading();
      try {
        if (data && valueBaseline) {
          // Año actual
          const currentYear = new Date().getFullYear();

          // 🔹 Últimos 5 años dinámicos
          const lastFiveYears = [
            currentYear,
            currentYear - 1,
            currentYear - 2,
            currentYear - 3,
            currentYear - 4,
          ];

          // Usar trabajadores compartidos desde DashBoard.js
          const workersByPeriodData = sharedWorkersByPeriod || {};
          setWorkersTotalsByPeriod(workersByPeriodData);

          // Calcular promedio de empleados por año desde los datos por periodo
          // (igual que en IntermediateValuesDisplay)
          const employeesByYear = {};
          for (const year in workersByPeriodData) {
            const workersValues = Object.values(
              workersByPeriodData[year] || {},
            );
            const workersTotal = workersValues.reduce((a, b) => a + b, 0);
            const workersMonthsWithData = workersValues.filter(
              (v) => v > 0,
            ).length;
            employeesByYear[year] =
              workersMonthsWithData > 0
                ? workersTotal / workersMonthsWithData
                : 0;
          }
          setWorkersTotals(employeesByYear);

          const previous_totals = await transformData1(data); //separa por años los formularios
          calculateDataFinal(previous_totals, employeesByYear); //calcula los totales finales indicadores con empleados

          // 🔹 Filtrar datos solo de esos 5 años
          const filteredByYears = data?.data
            ?.filter((item) => lastFiveYears.includes(Number(item.year)))
            ?.reduce((acc, item) => {
              const year = Number(item.year);
              if (!acc[year]) acc[year] = [];
              acc[year].push(item);
              return acc;
            }, {});

          // 🔹 Guardar solo años con al menos un mes cargado
          const yearsWithData = lastFiveYears.filter((year) =>
            data?.data?.some((item) => Number(item.year) === year)
          );
          setLastYears(yearsWithData);

          const results = await getLastThreeYearsData(filteredByYears);
          setTotalesByFilters(results);

          // Usar producción compartida desde DashBoard.js
          if (sharedProductionForms) {
            const totalesFinal = await calcularVPSVFSporMesPorAño(
              sharedProductionForms,
              baseline,
            );
            setTotalesProductionsByFilters(totalesFinal);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        stopLoading();
      }
    })();
  }, [
    data,
    valueBaseline,
    configFormData,
    sharedProductionForms,
    sharedWorkersByPeriod,
  ]);

  //Calcula el valor de linea base por año hay que cambiar si es por vfs o vps
  const getValueBaseLine = async (year, type) => {
    try {
      if (type === "VPS") {
        const response1 =
          await productionController.getTotalBaselineByYearAndSite(
            accessToken,
            site,
            year,
            "standardized_production",
          );
        //setValueBaseline(response1.total)
        return response1.total;
      } else {
        const response1 =
          await productionController.getTotalBaselineByYearAndSite(
            accessToken,
            site,
            year,
            "standardized_billing",
          );
        //setValueBaseline(response1.total)
        return response1.total;
      }
    } catch (error) {
      //console.error(error);
      return 1;
    }
  };

  //Funcion para obtener los valores de los campos por cada año
  const transformData1 = async (data) => {
    // Inicializar el objeto de totales y los conceptos
    const totalsPerYear = {};
    const conceptsSet = [];
    const unitsCache = {}; // Aquí almacenaremos los datos de unidades por año

    // Unidades configuradas en configFormData tienen prioridad máxima
    const configUnits = {};
    if (configFormData?.fields && configFormData.fields.length > 0) {
      configFormData.fields.forEach((field) => {
        if (field.name && field.unit) {
          configUnits[field.name] = field.unit;
        }
      });
    }

    const fields = determineFieldsCode();

    // Verificar si data y data.data son válidos y contienen datos
    if (!data || !Array.isArray(data.data) || data.data.length === 0) {
      //console.log("No hay datos para procesar o data.data no es un array válido");
      return {}; // Retornar un objeto vacío o manejar el caso según se requiera
    }
    // Crear un array de años únicos de los datos de consumo
    const uniqueYears = [
      ...new Set(data.data.map((item) => String(item.year))),
    ];

    // Primero, obtener las unidades para todos los años requeridos
    //console.log('Obteniendo unidades para los años:', uniqueYears);
    const promises = uniqueYears.map(async (año) => {
      try {
        // Realizar la llamada para obtener las unidades para el año
        const units = await energyController.getLastUnitFieldsByYear(
          accessToken,
          site,
          año,
          fields,
        );

        // Guardar en el caché si las unidades son válidas
        if (units && units.fields) {
          unitsCache[año] = units;
          //console.log(`Unidades guardadas en caché para el año ${año}:`, units);
        } else {
          // console.log(`No se obtuvieron unidades válidas para el año ${año}`);
        }
      } catch (error) {
        //console.log(`Error al obtener unidades para el año ${año}:`, error);
      }
    });

    // Esperar a que todas las consultas de unidades se resuelvan
    await Promise.all(promises);

    // Ahora procesamos los datos de consumo usando las unidades en caché
    // console.log('Procesando los consumos');
    if (data.data !== undefined) {
      // Crear un array de promesas para manejar las llamadas asíncronas
      const consumptionPromises = data.data.map(async (item) => {
        const year = String(item.year);

        // Verificar si ya tenemos las unidades para este year en el caché
        const units = unitsCache[year];
        if (!units) {
          //console.log(`No se encontraron unidades para el year ${year} en el caché`);
          return;
        }

        // Iterar sobre los conceptos que tenemos en el item
        fields.forEach((concept) => {
          // Si el concepto existe, procesamos
          if (item[concept]) {
            // Añadir el concepto a la lista de concepts únicos
            conceptsSet.push(concept);

            // Inicializar el total para el year si no existe
            if (!totalsPerYear[concept]) {
              totalsPerYear[concept] = {};
            }

            // Inicializar el total para el year si no existe
            if (!totalsPerYear[concept][year]) {
              totalsPerYear[concept][year] = {
                total: 0,
                unit: configUnits[concept] || units.fields[concept] || item[concept]?.unit || "",
              };
            }

            // Sumar el valor de consumo
            totalsPerYear[concept][year].total += item[concept].value
              ? convertUnitsFinal(
                  item[concept].value,
                  item[concept].unit,
                  totalsPerYear[concept][year].unit,
                  concept,
                )
              : 0;
          }
        });
      });
      // Esperar a que todas las promesas de consumo se resuelvan
      await Promise.all(consumptionPromises);
    }

    return totalsPerYear;
  };

  const calculateDataFinal = async (totales, employeesByYear = {}) => {
    // Inicializar el objeto de totales y los conceptos
    const totalsFinal = { ...totales };
    const conceptsSet = [];

    if (totales !== undefined) {
      // Obtener años únicos de los datos
      const yearsSet = new Set();
      for (let item in totales) {
        if (totales.hasOwnProperty(item)) {
          for (let year in totales[item]) {
            yearsSet.add(year);
          }
        }
      }
      const years = Array.from(yearsSet);

      // Obtener los KPIs a calcular (solo una vez)
      const fields = determineFieldsKPIs();

      // Calcular cada KPI para cada año (solo una vez por combinación)
      fields.forEach((concept) => {
        if (!totalsFinal[concept]) {
          totalsFinal[concept] = {};
          conceptsSet.push(concept);
        }

        years.forEach((year) => {
          const valueB = valueBaseline[year];
          const valueE = employeesByYear[year] || 1;

          totalsFinal[concept][year] = energyFormulasIndicators(
            concept,
            year,
            totalsFinal,
            valueB === 0 ? 1 : valueB,
            valueE === 0 ? 1 : valueE,
          );
        });
      });
    }

    setConceptos(removeNulls(totalsFinal, conceptsSet));
    setTotales(totalsFinal);
  };

  // Función para obtener los valores de un año y mes específicos (period en texto)
  const getDataByYearAndMonth = async (data, year, month) => {
    const results = {};
    const fields = determineFieldsCode();
    const unitsCache = {};

    if (!data || !data || Object.keys(data).length === 0) {
      return {};
    }

    // Diccionario de meses
    const monthMap = {
      January: "01",
      February: "02",
      March: "03",
      April: "04",
      May: "05",
      June: "06",
      July: "07",
      August: "08",
      September: "09",
      October: "10",
      November: "11",
      December: "12",
    };

    const yearStr = String(year);
    const monthStr =
      typeof month === "string"
        ? monthMap[month] || ""
        : String(month).padStart(2, "0");

    if (!monthStr) {
      console.warn("Mes inválido o no reconocido:", month);
      return {};
    }

    const yearData = data[yearStr];
    if (!Array.isArray(yearData)) {
      console.warn("No hay datos para el año:", yearStr);
      return {};
    }

    const filteredData = yearData.filter(
      (item) => monthMap[item.period] === monthStr,
    );

    if (filteredData.length === 0) {
      console.warn(`No hay datos para ${month} ${yearStr}`);
      return {};
    }

    // Procesar resultados por campo
    filteredData.forEach((item) => {
      const units = unitsCache[yearStr];

      fields.forEach((concept) => {
        if (item[concept]) {
          if (!results[concept]) {
            results[concept] = {
              total: 0,
              unit: units?.fields[concept] || item[concept]?.unit || "",
            };
          }

          results[concept].total += item[concept].value
            ? convertUnitsFinal(
                item[concept].value,
                item[concept].unit,
                results[concept].unit,
                concept,
              )
            : 0;
        }
      });
    });

    // ==========================
    // 🔹 Calcular energía total (sin incluir costos)
    // ==========================

    // Lista de conceptos energéticos (sin costos)
    const energyConcepts = Object.keys(energyCodes).filter(
      (key) => !key.includes("cost"),
    );

    let totalEnergyMonth = 0;
    let energyUnit = "";

    energyConcepts.forEach((concept) => {
      if (results[concept] && typeof results[concept].total === "number") {
        totalEnergyMonth += results[concept].total;
        energyUnit = results[concept].unit || energyUnit;
      }
    });

    results.total_energy_month = {
      total: totalEnergyMonth,
      unit: energyUnit || "MWh",
    };

    // ==========================
    // 🔹 Calcular renovables
    // ==========================
    // Ejemplo: filtramos solo las claves que sean renovables
    const renewableConcepts = Object.keys(energyCodes).filter((key) =>
      key.includes("renewable"),
    );

    let renewableTotal = 0;
    let renewableUnit = "";

    renewableConcepts.forEach((concept) => {
      if (results[concept] && typeof results[concept].total === "number") {
        renewableTotal += results[concept].total;
        renewableUnit = results[concept].unit || renewableUnit;
      }
    });

    results.renewable_energy_month = {
      total: renewableTotal,
      unit: renewableUnit || "MWh",
    };

    // ==========================
    // 🔹 Calcular electrica
    // ==========================

    // Filtramos solo los conceptos eléctricos (electricidad estándar + energías renovables PPA + on-site)
    const electricConcepts = Object.keys(energyCodes).filter(
      (key) =>
        key.includes("electricity_standard") ||
        key.includes("renewable_energies_ppa") ||
        key.includes("renewable_energies_on_site"),
    );

    let electricTotal = 0;
    let electricUnit = "";

    electricConcepts.forEach((concept) => {
      if (results[concept] && typeof results[concept].total === "number") {
        electricTotal += results[concept].total;
        electricUnit = results[concept].unit || electricUnit;
      }
    });

    results.electrica = {
      total: electricTotal,
      unit: electricUnit || "MWh",
    };

    return results;
  };

  // ==========================
  // 🔹 Calcular ultimos 3 años
  // ==========================

  const getLastThreeYearsData = async (totalesByFilters) => {
    const currentYear = new Date().getFullYear();
    const lastThreeYears = [currentYear - 2, currentYear - 1, currentYear];

    const resultsByYear = {};

    for (const year of lastThreeYears) {
      resultsByYear[year] = {};

      for (const month of months) {
        const data = await getDataByYearAndMonth(totalesByFilters, year, month);
        resultsByYear[year][month] = data;
      }
    }

    return resultsByYear;
  };

  const removeNulls = (data, concepts) => {
    // console.log("🔍 === removeNulls === Verificando KPIs con filas vacías...");
    // console.log("📊 Total de KPIs antes del filtrado:", concepts.length);
    // console.log("📋 Conceptos recibidos:", concepts);
    const removedKPIs = [];

    // Iterar sobre las claves externas
    for (let key in data) {
      if (data.hasOwnProperty(key)) {
        // Verificar si todos los valores internos son 0
        const allZero = Object.values(data[key]).every(
          (value) =>
            value.total === 0 ||
            value.total === undefined ||
            isNaN(value.total),
        );

        // Si todos los valores son 0, eliminar la clave externa
        if (allZero) {
          // console.log(`❌ KPI eliminado (fila vacía): ${key}`);
          removedKPIs.push(key);
          concepts = concepts.filter((item) => item !== key);
        }
      }
    }

    // console.log("📊 Total de KPIs después del filtrado:", concepts.length);
    // console.log("🗑️ KPIs eliminados por tener fila vacía:", removedKPIs);
    // console.log("✅ KPIs visibles finales:", concepts);

    return concepts;
  };

  async function calcularVPSVFSporMesPorAño(documentosPorAño, baselines) {
    const resultado = {};

    for (const [year, docs] of Object.entries(documentosPorAño)) {
      const baselineType = baselines[year]; // "VPS" o "VFS"
      if (!baselineType) continue;

      const isProduccion = baselineType === "VPS";
      const prefix = isProduccion ? "VP" : "VF";

      resultado[year] = {};

      for (const doc of docs) {
        const mes = doc.period;
        if (!mes) continue;

        let totalMes = 0;

        // Recorremos todos los campos del documento
        for (const [key, campo] of Object.entries(doc)) {
          // buscamos solo los campos dinámicos de producción o facturación
          if (
            campo?.code &&
            campo.code.startsWith(prefix) &&
            !campo.code.startsWith(prefix + "S")
          ) {
            const value = Number(campo.value) || 0;
            const coef = Number(campo.standardized_coefficient) || 1;
            totalMes += value * coef;
          }
        }

        resultado[year][mes] = totalMes;
      }
    }

    return resultado;
  }

  // Refs para cada gráfico
  const tableGrafRef = useRef();
  const comparisonChartRef = useRef();
  const monthlyChartRef = useRef();

  const generarPDF = async () => {
    const pdf = new jsPDF("p", "mm", "a4"); // vertical, mm, tamaño A4
    let yOffset = 10; // posición inicial vertical

    const captureElement = async (ref) => {
      if (!ref.current) return null;
      const canvas = await html2canvas(ref.current, { scale: 2 });
      return canvas.toDataURL("image/png");
    };

    // Capturar gráficos
    const img1 = await captureElement(tableGrafRef);
    const img2 = await captureElement(comparisonChartRef);
    const img3 = await captureElement(monthlyChartRef);

    const pageWidth = pdf.internal.pageSize.getWidth();

    const addImageToPDF = (img) => {
      if (!img) return;
      const imgProps = pdf.getImageProperties(img);
      const pdfHeight = (imgProps.height * pageWidth) / imgProps.width;
      // Agregar imagen al PDF
      pdf.addImage(img, "PNG", 0, yOffset, pageWidth, pdfHeight);
      yOffset += pdfHeight + 10; // separar con un pequeño margen
      // Si supera la página, crear nueva página
      if (yOffset > pdf.internal.pageSize.getHeight() - 20) {
        pdf.addPage();
        yOffset = 10;
      }
    };

    [img1, img2, img3].forEach(addImageToPDF);

    pdf.save("reporte_energia.pdf");
  };

  // Función para obtener el icono de aumento o disminución
  const getIndicators = (item) => {
    let result = indicatorsLineBase.find((value) => value.name === item);
    if (result) {
      return result?.yearBaseline || 0;
    }
    return 0; // Sin cambio
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (
    !data ||
    !data.years ||
    data.years.length === 0 ||
    !conceptos ||
    conceptos.length === 0
  ) {
    return (
      <div style={{ textAlign: "center", padding: "5em 0", color: "#777" }}>
        <div style={{ fontSize: "2em", marginBottom: "1em", color: "#ccc" }}>
          <Icon name="exclamation triangle" size="huge" color="gray" />
        </div>
        <div
          style={{ fontSize: "1.5em", fontWeight: "bold", marginBottom: "1em" }}
        >
          {t("no_data_available") || "No hay datos disponibles"}
        </div>
      </div>
    );
  } else {
    return (
      <div className="list-energy-forms">

        {/* Sección de Resumen */}
        <div className="energy-summary-section">
          <ResumenEnergia
            filters={filters}
            site={site}
            valueBaseline={valueBaseline}
            energyController={Energyform}
            totales={totales}
            selectorSlot={<YearMonthSelector onChange={setFilters} />}
          />
        </div>

        {/* Sección de Gráficos */}
        <div className="energy-tablegraf-section" ref={tableGrafRef}>
          <TableGraf filters={filters} totales={totales} t={t} />
        </div>

        {/* Sección de Comparación */}
        <CollapsibleSection
          title={t("electric_energy_consumption_evolution")}
          className="energy-comparison-section"
          ref={comparisonChartRef}
        >
          <EnergyComparisonChart resultsByYear={totalesByFilters} t={t} />
        </CollapsibleSection>

        {/* Sección de Intensidad */}
        <CollapsibleSection
          title={t("electric_energy_intensity")}
          className="energy-intensity-section"
          ref={monthlyChartRef}
        >
          <EnergyMonthlyChart
            resultsByYear={totalesByFilters}
            t={t}
            resultsProductions={totalesProductionsByFilters}
          />
        </CollapsibleSection>

        {/* Sección de Tabla de Datos */}
        <CollapsibleSection
          className="energy-table-section"
          header={
            <div className="energy-table-col-header">
              <span className="col-code">{t("code")}</span>
              <span className="col-indicators">{t("indicators")}</span>
              <span className="col-years">
                {(data.years
                  ? [...data.years].sort((a, b) => b - a).slice(0, 5)
                  : []
                ).map((y) => (
                  <span key={y} className="col-year">{y}</span>
                ))}
              </span>
              <span className="col-lb">{t("LB")}</span>
              <span className="col-units">{t("units")}</span>
            </div>
          }
          defaultOpen={true}
        >
          <TableYears
            filters={filters}
            data={data}
            accessToken={accessToken}
            t={t}
            conceptos={conceptos}
            totales={totales}
            language={language}
            indicatorsLineBase={indicatorsLineBase}
            valueBaseline={valueBaseline}
            getIndicators={getIndicators}
            selectedUnits={selectedUnits}
            setSelectedUnits={setSelectedUnits}
          />
        </CollapsibleSection>

        {/* Sección de Valores Intermedios */}
        <IntermediateValuesDisplay
          resultsProductions={totalesProductionsByFilters}
          resultsWorkers={workersTotalsByPeriod}
          t={t}
        />

        {/* Sección de Coeficientes de Conversión */}
        <ConversionCoefficientsDisplay t={t} />
      </div>
    );
  }
}

function TableYears(props) {
  const {
    data,
    conceptos,
    totales,
    t,
    language,
    valueBaseline,
    indicatorsLineBase,
    filters,
    getIndicators,
    selectedUnits,
    setSelectedUnits,
  } = props;

  const { year, month } = filters || {};
  const [years, setYears] = useState([]);
  const [months, setMoths] = useState([]);
  const [listConceptos, setListConceptos] = useState([]);

  //const [uniqueFields, setUniqueFields] = useState([]);

  const handleUnitChange = (rowIndex, newValue) => {
    setSelectedUnits({ ...selectedUnits, [rowIndex]: newValue.value });
  };

  // useEffect(() => {
  //   console.log("📊 TableGraf filtrando con:", { year, month });
  // }, [year, month]);

  useEffect(() => {
    (async () => {
      try {
        setYears(data.years ? data.years.sort((a, b) => b - a).slice(0, 5) : []);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [data]);

  // Buscar el primer año que tenga unidad definida
  const obtenerUnidadDeOtrosAnios = (datos, concepto) => {
    if (!datos[concepto]) return "";
    const años = Object.keys(datos[concepto]);
    const añoConUnidad = años.find((año) => datos[concepto][año]?.unit);
    return añoConUnidad ? datos[concepto][añoConUnidad].unit : "";
  };

  useEffect(() => {
    (async () => {
      try {
        // Obtener las unidades buscando el primer año con unidad definida
        let valor = {};
        conceptos.map((field) => {
          valor[field] = obtenerUnidadDeOtrosAnios(totales, field);
        });
        setSelectedUnits(valor);
      } catch (error) {
        //console.error(error);
        setSelectedUnits([]);
      }
    })();
  }, [conceptos, totales]);

  useEffect(() => {
    (async () => {
      try {
        setListConceptos(conceptos);
      } catch (error) {
        console.error(error);
      }
    })();
  }, [conceptos]);


  // // Función para obtener el icono de aumento o disminución
  // const getIndicators = (item) => {
  //   let result = indicatorsLineBase.find((value) => value.name === item);
  //   if (result) {
  //     return result?.yearBaseline || 0;
  //   }
  //   return 0; // Sin cambio
  // };

  // Determina si una fila debe mostrarse como enteros (sin decimales)
  // Ej: filas de Costo ($) o Trabajadores
  const isIntegerRow = (item) => {
    return item.includes("cost");
  };

  // Determina los decimales necesarios para un valor individual
  // Muestra siempre 2 dígitos significativos después de los ceros iniciales
  // Ej: 0.00447 → 4 decimales → "0.0045", 0.0447 → 3 decimales → "0.045"
  const getNeededDecimals = (value) => {
    if (typeof value !== "number" || isNaN(value) || value === 0) return 2;
    const abs = Math.abs(value);
    if (abs < 0.0000005) return -1; // demasiado pequeño → mostrar 0
    if (abs >= 1) return 2;
    // Contar ceros iniciales después del punto decimal y agregar 2 dígitos
    const leadingZeros = Math.floor(-Math.log10(abs));
    return Math.max(2, leadingZeros + 2);
  };

  // Determina la cantidad de decimales consistente para toda una fila
  const getRowDecimalPlaces = (item, values) => {
    const nonZero = values.filter(
      (v) => typeof v === "number" && !isNaN(v) && v !== 0,
    );
    if (isIntegerRow(item)) {
      // Costos: enteros si todos los valores son >= 1; si hay valores < 1 aplica umbrales
      if (nonZero.length === 0 || nonZero.every((v) => Math.abs(v) >= 1))
        return 0;
    }
    let maxDecimals = 2;
    for (const v of values) {
      if (typeof v !== "number" || isNaN(v) || v === 0) continue;
      const needed = getNeededDecimals(v);
      if (needed === -1) continue;
      if (needed > maxDecimals) maxDecimals = needed;
    }
    return Math.min(maxDecimals, 6);
  };

  // Formatea un valor con la cantidad de decimales determinada para la fila
  const formatWithDecimals = (value, decimals) => {
    if (typeof value !== "number" || isNaN(value)) return "-";
    if (value === 0) return "-";
    // Si el valor es demasiado pequeño para 6 decimales, mostrar 0
    if (Math.abs(value) < 0.0000005)
      return decimals === 0 ? "0" : (0).toFixed(decimals);
    if (decimals === 0) return Math.round(value).toString();
    return value.toFixed(decimals);
  };

  const renderPill = (displayValue, prevDisplayValue, decimals) => {
    const numValue = displayValue != null ? Number(displayValue) : null;
    const numPrev = prevDisplayValue != null ? Number(prevDisplayValue) : null;

    if (numValue == null || numValue === 0) {
      return <span className="gei-value-pill pill-empty" />;
    }

    let pillClass;
    if (numPrev == null || numPrev === 0) {
      pillClass = "pill-neutral";
    } else if (numValue > numPrev) {
      pillClass = "pill-up";
    } else if (numValue < numPrev) {
      pillClass = "pill-down";
    } else {
      pillClass = "pill-neutral";
    }

    return (
      <span className={`gei-value-pill ${pillClass}`}>
        {formatWithDecimals(numValue, decimals)}
      </span>
    );
  };

  return (
    <>
      <Table celled structured size="small" className="gei-kpi-table">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell rowSpan="2" textAlign="center">
              {t("code")}
            </Table.HeaderCell>
            <Table.HeaderCell rowSpan="2" textAlign="center">
              {t("indicators")}
            </Table.HeaderCell>
            {years.map((year, index) => (
              <Table.HeaderCell rowSpan="2" textAlign="center" key={index}>
                {year}
              </Table.HeaderCell>
            ))}
            <Table.HeaderCell rowSpan="2" textAlign="center">
              {t("LB")}
            </Table.HeaderCell>
            <Table.HeaderCell
              rowSpan="2"
              textAlign="center"
              style={{ width: "200px" }}
            >
              {t("units")}
            </Table.HeaderCell>
            {/* <Table.HeaderCell rowSpan="2" textAlign="center">
              {t("MWh equivalentes")}
            </Table.HeaderCell> */}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {conceptos.map((item, index) => {
            // Recopilar todos los valores de la fila para determinar decimales consistentes
            const rowValues = years
              .map((y) => totales[item]?.[y]?.total)
              .filter((v) => typeof v === "number" && !isNaN(v));
            const lbTotal = totales[item]?.[getIndicators(item)]?.total;
            if (typeof lbTotal === "number" && !isNaN(lbTotal))
              rowValues.push(lbTotal);

            // Auto-downgrade de unidad: MWh → kWh cuando los valores son muy pequeños
            const baseUnit = (selectedUnits[item] || "").trim().toLowerCase();
            let unitFactor = 1;
            let displayUnit = selectedUnits[item] || "";
            let unitDowngraded = false;

            if (baseUnit === "mwh") {
              const nonZeroValues = rowValues.filter((v) => v !== 0);
              const maxAbsVal =
                nonZeroValues.length > 0
                  ? Math.max(...nonZeroValues.map(Math.abs))
                  : 0;
              if (maxAbsVal > 0 && maxAbsVal < 1) {
                unitFactor = 1000;
                displayUnit = "kWh";
                unitDowngraded = true;
              }
            }

            // Calcular decimales sobre los valores convertidos
            const displayValues = rowValues.map((v) => v * unitFactor);
            const rowDecimals = getRowDecimalPlaces(item, displayValues);

            return (
              <React.Fragment key={index}>
                <Table.Row>
                  <Table.Cell>{energyKPIsCodes[item]}</Table.Cell>
                  <Table.Cell>{t(item)}</Table.Cell>
                  {years.map((yearItem, i) => {
                    if (totales[item]) {
                      const rawValue = totales[item][yearItem]?.total || 0;
                      const prevRawValue = totales[item][yearItem - 1]?.total || 0;
                      const displayValue = rawValue * unitFactor;
                      const prevDisplayValue = prevRawValue * unitFactor;
                      const pill = renderPill(displayValue, prevDisplayValue, rowDecimals);
                      return (
                        <Table.Cell key={i} textAlign="center">
                          <Popup
                            content={`Valor real: ${parseFloat(Number(rawValue).toFixed(6))} ${totales[item][yearItem]?.unit || selectedUnits[item]}`}
                            trigger={<span style={{ cursor: "help" }}>{pill}</span>}
                            position="top center"
                            size="tiny"
                          />
                        </Table.Cell>
                      );
                    }
                    return <Table.Cell key={i}>{""}</Table.Cell>;
                  })}
                  <Table.Cell>
                    <Popup
                      content={`Valor real: ${parseFloat(Number(totales[item][getIndicators(item)]?.total || 0).toFixed(6))} ${totales[item][getIndicators(item)]?.unit || selectedUnits[item]}`}
                      trigger={
                        <span
                          style={{
                            cursor: "help",
                            ...(unitDowngraded ? { color: "#2d2d2d" } : {}),
                          }}
                        >
                          {formatWithDecimals(
                            (totales[item][getIndicators(item)]?.total || 0) *
                              unitFactor,
                            rowDecimals,
                          )}
                        </span>
                      }
                      position="top center"
                      size="tiny"
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <span
                      style={
                        unitDowngraded
                          ? { color: "#2d2d2d", fontWeight: "bold" }
                          : {}
                      }
                    >
                      {displayUnit
                        ? item.includes("per_employee") ||
                          item === "electricity_standard_domestic_kpis" ||
                          item === "renewable_energies_ppa_domestic_kpis" ||
                          item === "renewable_energies_on_site_domestic_kpis" ||
                          item === "natural_gas_domestic_kpis" ||
                          item === "glp_domestic_kpis"
                          ? `${displayUnit} / ${t("employee")}`
                          : item.includes("per_unit") ||
                              item === "electricity_standard_combined_kpis" ||
                              item === "electricity_standard_production_kpis" ||
                              item === "renewable_energies_ppa_combined_kpis" ||
                              item ===
                                "renewable_energies_ppa_production_kpis" ||
                              item ===
                                "renewable_energies_on_site_combined_kpis" ||
                              item ===
                                "renewable_energies_on_site_production_kpis" ||
                              item === "natural_gas_combined_kpis" ||
                              item === "natural_gas_cogeneration_kpis" ||
                              item ===
                                "natural_gas_production_operations_kpis" ||
                              item === "glp_combined_kpis" ||
                              item === "glp_cogeneration_kpis" ||
                              item === "glp_production_operations_kpis" ||
                              item === "heavy_fuel_oil_total_kpis" ||
                              item === "light_fuel_oil_total_kpis" ||
                              item === "coal_total_kpis" ||
                              item === "diesel_combined_kpis" ||
                              item === "diesel_cogeneration_kpis" ||
                              item === "diesel_production_operations_kpis" ||
                              item === "diesel_forklifts_kpis" ||
                              item === "biodiesel_combined_kpis" ||
                              item === "biodiesel_cogeneration_kpis" ||
                              item === "biodiesel_production_operations_kpis" ||
                              item === "biodiesel_forklifts_kpis" ||
                              item === "gasoline_combined_kpis" ||
                              item === "gasoline_cogeneration_kpis" ||
                              item === "gasoline_production_operations_kpis" ||
                              item === "gasoline_forklifts_kpis" ||
                              item === "biofuel_combined_kpis" ||
                              item === "biofuel_cogeneration_kpis" ||
                              item === "biofuel_production_operations_kpis" ||
                              item === "biofuel_forklifts_kpis" ||
                              item === "biomass_total_kpis" ||
                              item === "steam_total_kpis" ||
                              item === "total_energy_kpis"
                            ? `${displayUnit} / ${t("unit")}`
                            : displayUnit
                        : "-"}
                    </span>
                    {/* Dropdown para convertir unidades (comentado)
                  <Dropdown
                    onChange={(e, data) => {
                      handleUnitChange(item, data);
                    }}
                    options={determineEnergyUnits(item).map((ds) => {
                      return {
                        key: ds.key,
                        text: ds.name,
                        value: ds.value,
                      };
                    })}
                    search
                    inline
                    defaultValue={determineEnergyUnits(item)[0].value}
                  />
                  */}
                  </Table.Cell>

                  {/* <Table.Cell>
                  {(() => {
                    const mwhValue = convertEnergy2(
                      totales[item][year]?.total || 0,
                      totales[item][year]?.unit,
                      "mwh",
                      item,
                      true
                    );
                    return (
                      <Popup
                        content={`Valor exacto: ${mwhValue || 0}`}
                        trigger={
                          <span style={{ cursor: "help" }}>
                            {formatWithDecimals(mwhValue, rowDecimals)}
                          </span>
                        }
                        position="top center"
                        size="tiny"
                      />
                    );
                  })()}
                </Table.Cell> */}
                </Table.Row>
              </React.Fragment>
            );
          })}
        </Table.Body>
      </Table>
    </>
  );
}

export const YearMonthSelector = ({ onChange }) => {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const currentYear = new Date().getFullYear();

  const years = [
    { key: currentYear, text: currentYear, value: currentYear },
    { key: currentYear - 1, text: currentYear - 1, value: currentYear - 1 },
    { key: currentYear - 2, text: currentYear - 2, value: currentYear - 2 },
    { key: currentYear - 3, text: currentYear - 3, value: currentYear - 3 },
    { key: currentYear - 4, text: currentYear - 4, value: currentYear - 4 },
  ];

  const months = [
    { key: 1, text: t("January"), value: 1 },
    { key: 2, text: t("February"), value: 2 },
    { key: 3, text: t("March"), value: 3 },
    { key: 4, text: t("April"), value: 4 },
    { key: 5, text: t("May"), value: 5 },
    { key: 6, text: t("June"), value: 6 },
    { key: 7, text: t("July"), value: 7 },
    { key: 8, text: t("August"), value: 8 },
    { key: 9, text: t("September"), value: 9 },
    { key: 10, text: t("October"), value: 10 },
    { key: 11, text: t("November"), value: 11 },
    { key: 12, text: t("December"), value: 12 },
  ];

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // 1-12

  const handleYearChange = (e, { value }) => {
    setSelectedYear(value);
    onChange({ year: value });
  };

  return (
    <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
      <Dropdown
        placeholder="Año"
        selection
        options={years}
        value={selectedYear}
        onChange={handleYearChange}
        className="year-filter-dropdown"
      />
    </div>
  );
};

function EnergyComparisonChart({ resultsByYear, t }) {
  const years = Object.keys(resultsByYear);

  // ✅ Calcular los datos solo cuando cambien resultsByYear
  const { barData, lineData } = useMemo(() => {
    const bar = [];
    const line = [];
    const sortedYears = [...years].sort((a, b) => parseInt(b) - parseInt(a));

    sortedYears.forEach((year) => {
      const values = months.map(
        (month) => resultsByYear?.[year]?.[month]?.electrica?.total || 0,
      );
      const avg =
        values.reduce((a, b) => a + b, 0) /
        (values.filter((v) => v > 0).length || 1);

      months.forEach((month, i) => {
        bar.push({
          month: t(month),
          value: values[i],
          year: String(year), // 👈 Usado para leyenda y color
          tipo: "Consumo", // 👈 Usado para el tooltip
        });
        line.push({
          month: t(month),
          value: avg,
          year: String(year),
          tipo: "Promedio",
        });
      });
    });
    return { barData: bar, lineData: line };
  }, [resultsByYear, t]);

  // Renderizador de la leyenda estilo "Pills"
  const CustomLegend = () => (
    <div style={{
      display: "flex",
      justifyContent: "flex-start", // 👈 Alinea a la izquierda
      gap: "10px",
      marginBottom: "20px",
      paddingLeft: "5px", // Ajusta según el padding de tu contenedor de gráfico
      flexWrap: "wrap",
    }}>
      {years.map((year) => {
        const isActive = activeYears.includes(year);
        return (
          <div
            key={year}
            onClick={() => toggleYear(year)}
            style={{
              backgroundColor: isActive ? yearColorMap[year] : "#f3f4f6", 
              color: isActive ? "white" : "#9ca3af",
              padding: "4px 20px", // Un poco más compacto
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              border: isActive ? `1px solid ${yearColorMap[year]}` : "1px solid #d1d5db",
              boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth:"100px"
            }}
          >
            {year}
          </div>
        );
      })}
    </div>
  );
  
  // Dentro de tu componente FuelsComparisonChart
  const [activeYears, setActiveYears] = useState(years); 
  
  // Función para alternar años
  const toggleYear = (year) => {
    if (activeYears.includes(year)) {
      // Si solo queda uno, podrías elegir no dejar que se desactive el último
      setActiveYears(activeYears.filter(y => y !== year));
    } else {
      setActiveYears([...activeYears, year]);
    }
  };
  
  const filteredBarData = useMemo(() => 
    barData.filter(d => activeYears.includes(String(d.year))), 
  [barData, activeYears]);
  
  const filteredLineData = useMemo(() => 
    lineData.filter(d => activeYears.includes(String(d.year))), 
  [lineData, activeYears]);
    // 🔹 Leer colores desde CSS
  const getCssVar = (name) =>
    getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();
  
  const chartColors = {
    oldest: getCssVar("--chart-year-oldest"),
    prev: getCssVar("--chart-year-prev"),
    last: getCssVar("--chart-year-last"),
    avg: getCssVar("--chart-avg-line"),
  };
  

  // 1. Mapear años → colores fijos (Asegúrate de que esto esté actualizado)
  const yearColorMap = useMemo(() => {
    const sorted = [...years].map(Number).sort((a, b) => a - b);
    return {
      [sorted[0]]: chartColors.oldest || "#cbd5e1", // Fallback si el CSS no carga
      [sorted[1]]: chartColors.prev || "#94a3b8",
      [sorted[2]]: chartColors.last || "#1e293b",
    };
  }, [years, chartColors]);
  

  const config = {
    xField: "month",
    //inset: 12,
    legend:false,
    autoFit: true,
  padding: 'auto', // Deja que la librería calcule el espacio
  appendPadding: [20, 0, 20, 0], // [arriba, derecha, abajo, izquierda]
   scale: {
    color: {
      domain: Object.keys(yearColorMap),
      range: Object.values(yearColorMap),
    },
      y: { sync: true, nice: true, domainMin: 0 },
      
  },
    tooltip: {
      shared: true,
      // Personalizamos los items del tooltip
      items: [
        (item) => ({
          // Combinamos el tipo y el año: "Consumo 2026" o "Promedio 2026"
          name: `${item.tipo} ${item.year}`,
          value: `${Math.round(item.value).toLocaleString("es-AR")} kWh`,
          //color: item.color,
        }),
      ],
    },
    children: [
      {
        data: filteredBarData,
        type: "interval",
        yField: "value",
        colorField: "year", // 👈 Leyenda por Año
        group: true,
        style: { maxWidth: 40 },
          scale: {
        color: ({ year }) => yearColorMap[year], // 👈 ACA
        }
      },
      {
        data: filteredLineData,
        type: "line",
        yField: "value",
        colorField: "year", // 👈 Mismo color que la barra del mismo año
        axis: { y: false },
       style: {
        lineWidth: 2,
        lineDash: [4, 8],
      },
      },
    ],
    axis: {
  x: {
    title: false,
    labelAutoHide: false,
    labelAutoRotate: false,
    labelFill: "#000",
  },
  y: {
      title: false,
      grid: true,
      labelFill: "#000",
    }
},
  };

  return (
    <div style={{ marginTop: 20, marginBottom: 40 }}>
          {/* 🔘 Selector de combustible */}
      
    {/* 🟢 NUEVA LEYENDA ESTILO IMAGEN */}
        {barData.length > 0 && <CustomLegend />}
    
          {barData.length ? (
            <div style={{ height: 400 }}>
             <DualAxes {...config}  />
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: 400,
                color: "#999",
              }}
            >
              No hay datos disponibles
            </div>
          )}
        </div>
  );
}

function EnergyMonthlyChart({ resultsByYear, t, resultsProductions }) {
  const years = Object.keys(resultsByYear);

  const { consumptionData, averageData } = useMemo(() => {
    const cData = [];
    const aData = [];

    const sortedYears = [...years].sort((a, b) => parseInt(b) - parseInt(a));

    sortedYears.forEach((year) => {
      const totals = months.map((m) => {
        const energy = resultsByYear?.[year]?.[m]?.electrica?.total ?? null;

        const production = resultsProductions?.[year]?.[m] ?? null;

        if (!energy || !production || production === 0) {
          return null; // 🔹 no dibuja caída a cero
        }

        return energy / production;
      });

      // 🔹 Filtrar solo valores válidos
      const validValues = totals.filter((v) => v !== null && !isNaN(v));

      const monthsWithData = validValues.length || 1;

      const avg = validValues.reduce((a, b) => a + b, 0) / monthsWithData;

      months.forEach((month, i) => {
        // En tu mapeo de cData y aData, asegúrate de pasar 'year' y 'tipo'
cData.push({
  year: String(year),
  month: t(month),
  value: totals[i],
  tipo: "Consumo", // Campo extra para identificar
  type: `Consumo ${year}`, // El que ya usas
});

aData.push({
  year: String(year),
  month: t(month),
  value: validValues.length > 0 ? avg : null,
  tipo: "Promedio", // Campo extra para identificar
  type: `Promedio ${year}`, // El que ya usas
});
      });
    });

    return { consumptionData: cData, averageData: aData };
  }, [resultsByYear, resultsProductions]);

  const allAverages = averageData.map((d) => d.value).filter((v) => v !== null);

  const overallAverage =
    allAverages.length > 0
      ? allAverages.reduce((a, b) => a + b, 0) / allAverages.length
      : 0;

  let yMin, yMax;

  if (overallAverage >= 120 && overallAverage <= 150) {
    yMin = 110;
    yMax = 160;
  } else {
    yMin = undefined;
    yMax = undefined;
  }

  
    // Renderizador de la leyenda estilo "Pills"
  const CustomLegend = () => (
    <div style={{
      display: "flex",
      justifyContent: "flex-start", // 👈 Alinea a la izquierda
      gap: "10px",
      marginBottom: "20px",
      paddingLeft: "5px", // Ajusta según el padding de tu contenedor de gráfico
      flexWrap: "wrap",
    }}>
      {years.map((year) => {
        const isActive = activeYears.includes(year);
        return (
          <div
            key={year}
            onClick={() => toggleYear(year)}
            style={{
              backgroundColor: isActive ? yearColorMap[year] : "#f3f4f6", 
              color: isActive ? "white" : "#9ca3af",
              padding: "4px 20px", // Un poco más compacto
              borderRadius: "20px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              border: isActive ? `1px solid ${yearColorMap[year]}` : "1px solid #d1d5db",
              boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth:"100px"
            }}
          >
            {year}
          </div>
        );
      })}
    </div>
  );
  
  // Dentro de tu componente FuelsComparisonChart
  const [activeYears, setActiveYears] = useState(years); 
  
  // Función para alternar años
  const toggleYear = (year) => {
    if (activeYears.includes(year)) {
      // Si solo queda uno, podrías elegir no dejar que se desactive el último
      setActiveYears(activeYears.filter(y => y !== year));
    } else {
      setActiveYears([...activeYears, year]);
    }
  };
  
  const filteredConsumptionData = useMemo(() => 
    consumptionData.filter(d => activeYears.includes(String(d.year))), 
  [consumptionData, activeYears]);
  
  const filteredAverageData = useMemo(() => 
    averageData.filter(d => activeYears.includes(String(d.year))), 
  [averageData, activeYears]);
    // 🔹 Leer colores desde CSS
  const getCssVar = (name) =>
    getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();
  
  const chartColors = {
    oldest: getCssVar("--chart-year-oldest"),
    prev: getCssVar("--chart-year-prev"),
    last: getCssVar("--chart-year-last"),
    avg: getCssVar("--chart-avg-line"),
  };
  ;
  
  // 1. Mapear años → colores fijos (Asegúrate de que esto esté actualizado)
  const yearColorMap = useMemo(() => {
    const sorted = [...years].map(Number).sort((a, b) => a - b);
    return {
      [sorted[0]]: chartColors.oldest || "#cbd5e1", // Fallback si el CSS no carga
      [sorted[1]]: chartColors.prev || "#94a3b8",
      [sorted[2]]: chartColors.last || "#1e293b",
    };
  }, [years, chartColors]);
  
  

 const config = {
  xField: "month",
  // 1. Sincronización global de la escala Y
     // 🔹 Escala global de colores para ambas capas
  scale: {
    color: {
      domain: Object.keys(yearColorMap),
      range: Object.values(yearColorMap),
    },
  },
  // 2. Configuración del eje visible
  axis: {
    x: {
      labelFill: "#000",
    },
    y: {
      labelFill: "#000",
      labelFormatter: (v) =>
        Number(v).toLocaleString("es-AR", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
  },
  tooltip: {
  shared: true,
  showMarkers: true,
  items: [
    (item, index, items) => {
      // Ordenamos la lista completa solo cuando procesamos el primer elemento
      if (index === 0) {
        items.sort((a, b) => {
          // 1. Extraer años
          const yearA = parseInt(a.year || a.type.match(/\d{4}/)?.[0] || 0);
          const yearB = parseInt(b.year || b.type.match(/\d{4}/)?.[0] || 0);

          // 2. Si son años diferentes, año mayor primero
          if (yearA !== yearB) return yearB - yearA;

          // 3. Si es el mismo año, Consumo va antes que Promedio
          const isAvgA = a.type.includes("Promedio") ? 1 : 0;
          const isAvgB = b.type.includes("Promedio") ? 1 : 0;
          return isAvgA - isAvgB;
        });
      }

      // Estética del item
      const isAvg = item.type.includes("Promedio");
      
      return {
        // Nombre limpio: "Consumo 2024" o "Promedio 2024"
        name: isAvg ? `--- Promedio ${item.year}` : `Consumo ${item.year}`,
        value: item.value !== null 
          ? Number(item.value).toLocaleString("es-AR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : "-", // Si no hay producción, mostrar guion
        color: yearColorMap[item.year],
      };
    },
  ],
},
   legend:false,
  children: [
    {
      data: filteredConsumptionData,
      type: "line",
      yField: "value",
      colorField: "year",
      connectNulls: false,
      style: { lineWidth: 4 },
      // Aquí ya no hace falta el scale local, lo toma del global
    },
    {
      data: filteredAverageData,
      type: "line",
      yField: "value",
      colorField: "year",
      // 3. QUITAMOS independent: true y AGREGAMOS axis: { y: false }
      axis: { y: false },
      style: {
        lineWidth: 2,
        lineDash: [4, 8],
      },
      connectNulls: false,
    },
  ],
};

  return (
     <div style={{ marginTop: 20, marginBottom: 40 }}>
       <div style={{
         display: "flex",
         justifyContent: "center",
         gap: "1rem",
         marginBottom: "1.5rem",
       }}>
      
       </div>
       {/* 🟢 NUEVA LEYENDA ESTILO IMAGEN */}
     {consumptionData.length > 0 && <CustomLegend />}
 
       {consumptionData.length ? (
         <DualAxes {...config} style={{ height: 400 }} />
       ) : (
         <div style={{
           display: "flex",
           alignItems: "center",
           justifyContent: "center",
           height: 400,
           color: "#999",
         }}>
           No hay datos disponibles
         </div>
       )}
     </div>
  );
}

// Componente para mostrar valores intermedios utilizados en los cálculos
function IntermediateValuesDisplay({ resultsProductions, resultsWorkers, t }) {
  const [isOpen, setIsOpen] = useState(false);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Obtener todos los años disponibles y ordenarlos de mayor a menor
  const availableYears = Object.keys(resultsProductions || {}).sort(
    (a, b) => b - a,
  );

  return (
    <div
      style={{
        margin: "20px 0",
        padding: "15px",
        backgroundColor: "#f8f9fa",
        borderRadius: "8px",
        border: "1px solid #dee2e6",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          padding: "10px 0",
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <h4 style={{ margin: 0, color: "#495057" }}>
          <Icon name={isOpen ? "chevron down" : "chevron right"} />
          Valores intermedios para verificación de cálculos - Últimos 5 años
        </h4>
        <span style={{ fontSize: "0.9em", color: "#6c757d" }}>
          {isOpen ? "Haz clic para ocultar" : "Haz clic para ver detalles"}
        </span>
      </div>

      {isOpen && (
        <>
          {availableYears.map((year) => (
            <div key={year} style={{ marginBottom: "30px" }}>
              <h5
                style={{
                  marginTop: "20px",
                  marginBottom: "10px",
                  color: "#495057",
                }}
              >
                Año {year}
              </h5>
              <Table celled size="small" style={{ fontSize: "0.9em" }}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell textAlign="center">Mes</Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">
                      Producción (VPS/VFS)
                    </Table.HeaderCell>
                    <Table.HeaderCell textAlign="center">
                      Trabajadores
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {months.map((month) => {
                    const production = resultsProductions?.[year]?.[month] || 0;
                    const workers = resultsWorkers?.[year]?.[month] || 0;

                    return (
                      <Table.Row key={month}>
                        <Table.Cell textAlign="center">
                          {t ? t(month) : month}
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {production > 0
                            ? parseFloat(production.toFixed(2))
                            : "-"}
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          {workers > 0 ? workers : "-"}
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                  {(() => {
                    const productionValues = Object.values(
                      resultsProductions?.[year] || {},
                    );
                    const workersValues = Object.values(
                      resultsWorkers?.[year] || {},
                    );

                    const productionTotal = productionValues.reduce(
                      (a, b) => a + b,
                      0,
                    );
                    const workersTotal = workersValues.reduce(
                      (a, b) => a + b,
                      0,
                    );

                    const productionMonthsWithData = productionValues.filter(
                      (v) => v > 0,
                    ).length;
                    const workersMonthsWithData = workersValues.filter(
                      (v) => v > 0,
                    ).length;

                    const productionAvg =
                      productionMonthsWithData > 0
                        ? productionTotal / productionMonthsWithData
                        : 0;
                    const workersAvg =
                      workersMonthsWithData > 0
                        ? workersTotal / workersMonthsWithData
                        : 0;

                    return (
                      <>
                        <Table.Row
                          style={{
                            fontWeight: "bold",
                            backgroundColor: "#e9ecef",
                          }}
                        >
                          <Table.Cell textAlign="center">Total</Table.Cell>
                          <Table.Cell textAlign="center">
                            {productionTotal > 0
                              ? parseFloat(productionTotal.toFixed(2))
                              : "-"}
                          </Table.Cell>
                          <Table.Cell textAlign="center">
                            {workersTotal > 0 ? workersTotal : "-"}
                          </Table.Cell>
                        </Table.Row>
                        <Table.Row
                          style={{
                            fontWeight: "bold",
                            backgroundColor: "#d4edda",
                          }}
                        >
                          <Table.Cell textAlign="center">Promedio</Table.Cell>
                          <Table.Cell textAlign="center">
                            {productionAvg > 0
                              ? parseFloat(productionAvg.toFixed(2))
                              : "-"}
                          </Table.Cell>
                          <Table.Cell textAlign="center">
                            {workersAvg > 0 ? workersAvg.toFixed(0) : "-"}
                          </Table.Cell>
                        </Table.Row>
                      </>
                    );
                  })()}
                </Table.Body>
              </Table>
            </div>
          ))}

          <div
            style={{
              fontSize: "0.85em",
              color: "#6c757d",
              marginTop: "10px",
              textAlign: "center",
            }}
          >
            <strong>Nota:</strong> Estos valores se utilizan como denominadores
            en los cálculos de intensidad. Producción (VPS/VFS) para intensidad
            industrial y Trabajadores para intensidad doméstica.
          </div>
        </>
      )}
    </div>
  );
}

// Componente para mostrar los coeficientes de conversión de energía
function ConversionCoefficientsDisplay({ t }) {
  const [isOpen, setIsOpen] = useState(false);

  // Nombres amigables para cada fuente de energía
  const fuelNames = {
    electricity: "Electricidad",
    renewable_energies: "Energías Renovables",
    natural_gas: "Gas Natural",
    glp_l: "GLP (Litros)",
    glp_kg: "GLP (Kilogramos)",
    heavy_fuel_oil: "Fuel Oil Pesado",
    light_fuel_oil: "Fuel Oil Liviano",
    coal: "Carbón",
    diesel: "Diesel",
    biodiesel: "Biodiesel",
    gasoline: "Gasolina",
    biofuel: "Biocombustible",
    biomass: "Biomasa",
    steam: "Vapor",
  };

  // Unidades para cada fuente
  const fuelUnits = {
    electricity: "kWh",
    renewable_energies: "kWh",
    natural_gas: "m³",
    glp_l: "L",
    glp_kg: "kg",
    heavy_fuel_oil: "L",
    light_fuel_oil: "L",
    coal: "kg",
    diesel: "L",
    biodiesel: "L",
    gasoline: "L",
    biofuel: "L",
    biomass: "kg",
    steam: "kg",
  };

  return (
    <div
      style={{
        margin: "20px 0",
        padding: "15px",
        backgroundColor: "#f0f7ff",
        borderRadius: "8px",
        border: "1px solid #b8daff",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          padding: "10px 0",
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <h4 style={{ margin: 0, color: "#004085" }}>
          <Icon name={isOpen ? "chevron down" : "chevron right"} />
          Coeficientes de Conversión de Energía a MWh
        </h4>
        <span style={{ fontSize: "0.9em", color: "#6c757d" }}>
          {isOpen ? "Haz clic para ocultar" : "Haz clic para ver detalles"}
        </span>
      </div>

      {isOpen && (
        <>
          <Table
            celled
            size="small"
            style={{ fontSize: "0.9em", marginTop: "15px" }}
          >
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell textAlign="center">
                  Fuente de Energía
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Unidad</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">
                  Coeficiente a MWh
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="center">
                  Equivalencia
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {Object.entries(CONVERSION_FACTORS_TO_MWH).map(
                ([fuel, coefficient]) => (
                  <Table.Row key={fuel}>
                    <Table.Cell textAlign="left" style={{ fontWeight: "500" }}>
                      {fuelNames[fuel] || fuel}
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      {fuelUnits[fuel] || FUELS[fuel]?.unit || "-"}
                    </Table.Cell>
                    <Table.Cell
                      textAlign="center"
                      style={{ fontFamily: "monospace" }}
                    >
                      {coefficient.toFixed(5)}
                    </Table.Cell>
                    <Table.Cell
                      textAlign="center"
                      style={{ color: "#6c757d", fontSize: "0.85em" }}
                    >
                      1 {fuelUnits[fuel] || "unidad"} = {coefficient.toFixed(5)}{" "}
                      MWh
                    </Table.Cell>
                  </Table.Row>
                ),
              )}
            </Table.Body>
          </Table>

          <div
            style={{
              fontSize: "0.85em",
              color: "#004085",
              marginTop: "10px",
              textAlign: "center",
              backgroundColor: "#cce5ff",
              padding: "10px",
              borderRadius: "4px",
            }}
          >
            <strong>Nota:</strong> Estos coeficientes se utilizan en la función
            convertEnergy2 para convertir cada tipo de combustible/fuente a MWh
            equivalentes.
          </div>
        </>
      )}
    </div>
  );
}
